import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { addAdminOrderNote } from "@/lib/admin-api-client";
import { describeAdminError } from "./adminErrors";

/**
 * Formulaire d'ajout de note interne sur une commande (`POST
 * /admin/orders/:id/notes`). N'effectue AUCUNE transition de statut : simple
 * annotation de suivi ajoutée au journal `OrderEvent` (type `NOTE`). Porté
 * depuis apps/web/components/admin/AdminOrderNoteForm.tsx —
 * `addOrderNoteAction` (Server Action) remplacée par `useMutation` direct,
 * `router.refresh()` → `invalidateQueries`.
 */
export function AdminOrderNoteForm({ orderId }: { orderId: string }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (trimmed: string) => addAdminOrderNote(undefined, orderId, trimmed),
    onSuccess: () => {
      setNote("");
      queryClient.invalidateQueries({ queryKey: ["admin", "order", orderId] });
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setValidationError(null);

    const trimmed = note.trim();
    if (!trimmed) {
      setValidationError("Entrez une note avant de l'enregistrer.");
      return;
    }

    mutation.mutate(trimmed);
  }

  const error = validationError ?? (mutation.isError ? describeAdminError(mutation.error) : null);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <label htmlFor="admin-order-note" className="text-xs font-medium uppercase tracking-wide text-navy">
        Ajouter une note interne
      </label>
      <textarea
        id="admin-order-note"
        name="note"
        rows={3}
        maxLength={1000}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Note visible uniquement par l'équipe VVS…"
        className="rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink focus:border-gold focus:outline-none"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? "admin-order-note-error" : undefined}
      />

      {error ? (
        <p id="admin-order-note-error" role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <div>
        <Button type="submit" variant="secondary" size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? "Enregistrement…" : "Ajouter la note"}
        </Button>
      </div>
    </form>
  );
}
